import { faStar } from '@fortawesome/free-regular-svg-icons'
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons/faCloudUploadAlt'
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo'
import cn from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { useMediaPredicate } from 'react-media-hook'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import { useSearchParams } from 'react-router-dom'

import useText from '../../../common/hooks/useText'
import { useProductGetOptionsQuery } from '../../../common/redux/api/web-api'
import { ProductVariant } from '../../../lib/loadProducts'
import MainPageLayout from '../../../ui/MainPage.layout'
import ButtonGroup from '../../../ui/common/ButtonGroup'
import UploadFirmwareTab from '../../../ui/firmware/UploadFirmwareTab'
import Tab from '../../../ui/Tab'
import SelectFactoryFirmwareTab from '../../../ui/firmware/SelectFactoryFirmwareTab'
import UpdateChargersFirmwareTab from '../../../ui/firmware/UpdateChargersFirmwareTab'
import { PersistentTabs as Tabs } from '../../../ui/Tabs'

export type Product = {
    productId: number | null
    firmwareType: FirmwareType
    ocppEnabledFirmware?: boolean
    description: string | null
    title: string | null
    firmwareVersion: string | null
    file: File | null
    firmwareServerLocationId: number | null
}

export const DEFAULT_VISIBLE_ITEMS = 3

const Select = styled(ReactSelect)`
    font-weight: 500;
`

const SDeviceTitleName = styled.h2`
    font-family: ${({ theme }) => theme.font.extendedName};
    font-size: 36px;
    font-weight: 500;
    line-height: 36px;
`

const SSubtitleTip = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    max-width: 600px;
`

const SFirmwareFilter = styled.div`
    margin-bottom: 30px;
`

const SFilterLabel = styled.label`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 300;
    line-height: 36px;
    margin-top: 1em;
    display: block;
`

export enum FirmwareType {
    Master = 'Master',
    Slave = 'Slave',
    Wifi = 'Wifi',
}

export enum ProductName {
    HALO = 'HALO',
    HALO_CAPI = 'HALO 2 CAPI',
    HALO_OCPP = 'HALO 2 OCPP',
    AURA_CAPI = 'AURA CAPI',
    AURA_OCPP = 'AURA OCPP',
    DAWN = 'DAWN',
}

type FilterState = {
    activeTab?: string
    productName?: string
    firmwareType?: FirmwareType
    productId?: number
}

const mapSearchParamsToFilter = ({ activeTab, productName, firmwareType, productId }: FilterState): FilterState => {
    return {
        activeTab,
        productName,
        firmwareType,
        productId,
    }
}

const productSort = Object.values(ProductName)

const FirmwarePage = (): React.ReactElement => {
    const text = useText()
    const [searchParams, setParams] = useSearchParams()
    const params = Object.fromEntries(searchParams.entries())
    const { activeTab, productName, firmwareType, productId } = mapSearchParamsToFilter(params)
    const isDesktopScreen = useMediaPredicate('(min-width: 900px)')
    const { data: products } = useProductGetOptionsQuery({ productVariant: ProductVariant.Chargepoint })
    const [firmwareTypes, setFirmwareTypes] = useState<FirmwareType[]>([])
    const firmwareTypeOptions = firmwareTypes.map((x) => ({ value: x, label: x }))
    const productsSelectOptions = useMemo(
        () =>
            products
                ?.slice()
                ?.sort(
                    (a, b) =>
                        productSort.indexOf(productSort.find((p) => p === a.displayName) ?? productSort[0]) -
                        productSort.indexOf(productSort.find((p) => p === b.displayName) ?? productSort[0])
                )
                .map((product) => ({
                    label: product.displayName,
                    value: product.displayName,
                })),

        [products]
    )

    const [currentProduct, setCurrentProduct] = useState<Partial<Product>>({
        productId: productId,
        firmwareType: undefined,
        ocppEnabledFirmware: undefined,
        description: undefined,
        title: undefined,
        firmwareVersion: undefined,
        file: undefined,
        // TODO: Implement server selecting later
        firmwareServerLocationId: 1,
    })

    useEffect(() => {
        if (products && products?.length > 0) {
            const product = products.find((item) =>
                productName ? item.displayName === productName : item.displayName === ProductName.HALO
            )
            if (product) {
                setFirmwareTypes(product.firmwareTypes ?? [])
                setCurrentProduct((prevValue) => ({
                    ...prevValue,
                    productId: product.productId,
                    ocppEnabledFirmware: product.ocppEnabled,
                    description: product?.displayName,
                    title: product?.displayName,
                    firmwareType: firmwareType ?? FirmwareType.Master,
                }))
            }
        }
    }, [firmwareType, productName, products])

    const onChangeFirmwareUploadForm = (fieldName: keyof Product, value: unknown) => {
        setCurrentProduct((prevValue) => ({ ...prevValue, [fieldName]: value }))
    }

    const onProductChange = (option: { value: ProductName }) => {
        const selectedProduct = products?.find((product) => product.displayName === option.value)
        if (!selectedProduct) return
        setParams({ ...params, productName: option.value, productId: selectedProduct.productId })
        setFirmwareTypes(selectedProduct.firmwareTypes)
        setCurrentProduct((prevValue) => ({
            ...prevValue,
            productId: selectedProduct.productId,
            ocppEnabledFirmware: selectedProduct.ocppEnabled,
            title: selectedProduct.displayName,
            description: selectedProduct.displayName,
            file: undefined,
            firmwareVersion: undefined,
            firmwareType: FirmwareType.Master,
        }))
    }

    return (
        <MainPageLayout>
            <div className="flex">
                <div className="w-12">
                    <SDeviceTitleName data-testid="title">{currentProduct.title}</SDeviceTitleName>
                </div>
            </div>
            <div className="flex">
                <div className="w-12 sm:w-8">
                    <SSubtitleTip data-testid="subtitle">{text('msgSelectProductAndFirmware')}</SSubtitleTip>
                </div>
            </div>
            <SFirmwareFilter className="flex">
                <div className="w-12 sm:w-4 md:w-3">
                    <div className="firmwareUploadForm-group">
                        <SFilterLabel data-testid="select-title">{text('lblSelectProduct')}</SFilterLabel>
                        {productsSelectOptions && productsSelectOptions.length > 0 ? (
                            <Select
                                data-testid="select"
                                isSearchable={false}
                                options={productsSelectOptions}
                                defaultValue={productName ?? productsSelectOptions[0]}
                                value={productsSelectOptions?.find((option) =>
                                    productName ? option.value === productName : option.value === currentProduct.title
                                )}
                                onChange={(newValue) => onProductChange(newValue)}
                            />
                        ) : null}
                    </div>
                </div>
                {firmwareTypeOptions.length > 0 ? (
                    <div className="w-12 sm:w-4 md:w-3 ml-8">
                        <div className="firmwareUploadForm-group">
                            <SFilterLabel data-testid="select-type">{text('lblSelectFirmwareType')}</SFilterLabel>
                            <ButtonGroup
                                value={firmwareType ?? currentProduct.firmwareType}
                                options={firmwareTypeOptions}
                                onChange={(data) => {
                                    setParams({ ...params, firmwareType: data?.value })
                                    onChangeFirmwareUploadForm('firmwareType', data?.value)
                                }}
                                withCheckmark
                            />
                        </div>
                    </div>
                ) : null}
            </SFirmwareFilter>

            <div className="flex">
                <div className={cn(isDesktopScreen && 'w-12')} data-testid="tabs">
                    <Tabs
                        active={activeTab}
                        onTabClick={(label) => {
                            setParams({ ...params, activeTab: label })
                        }}
                    >
                        <Tab label="Upload firmware" icon={faCloudUploadAlt} data-testid="upload">
                            <UploadFirmwareTab product={currentProduct} />
                        </Tab>
                        <Tab label="Update chargers" icon={faRedo}>
                            <UpdateChargersFirmwareTab product={currentProduct} />
                        </Tab>
                        <Tab label="Select factory firmware" icon={faStar} hidden={currentProduct.ocppEnabledFirmware}>
                            <SelectFactoryFirmwareTab product={currentProduct} />
                        </Tab>
                    </Tabs>
                </div>
            </div>
        </MainPageLayout>
    )
}

export default FirmwarePage
